// Leitor do PLACEHOLDER da calculadora de avaliação (CONTRATO-AVM-SITE.md §1).
//
// O HTML das "Páginas do Site" entra no site por dangerouslySetInnerHTML, que NÃO executa
// <script>. O builder (imobtotal-web) grava então um bloco morto:
//
//   <div class="itp-avm-calc" data-itp-block="avm-calculator" data-avm="{&quot;v&quot;:1,…}">
//     <div class="itp-avm-fallback">…texto com o link do WhatsApp…</div>
//   </div>
//
// e o site troca esse bloco INTEIRO pelo componente React. Quando qualquer coisa aqui falha
// (sem placeholder, JSON quebrado, versão desconhecida, token vazio = tenant não habilitado)
// devolvemos `null` e a página segue renderizando o HTML como sempre — o visitante vê o
// texto de fallback do próprio placeholder, que já traz o contato da imobiliária.
//
// ⚠️ Arquivo PURO (sem React, sem DOM, sem imports de runtime): roda no servidor, no
// navegador e no teste `node scripts/test-avm-placeholder.mjs`.

import type { AvmConfig, AvmSplit, AvmTheme } from './types';

/** Defaults do contrato §1 — tudo fora de `v`/`codsite`/`token` é opcional no JSON. */
export const AVM_THEME_PADRAO: AvmTheme = {
    primary: '#0b3d5c',
    secondary: '#c9a24b',
    bg: '#ffffff',
    text: '#1f2937',
    surface: '#f8fafc',
    muted: '#6b7280',
};

export const AVM_TEXTOS_PADRAO = {
    titulo: 'Quanto vale o seu imóvel?',
    subtitulo:
        'Responda algumas perguntas e receba uma estimativa na hora, baseada em imóveis reais da região.',
    perguntaAnunciar: 'Quer anunciar seu imóvel com a gente?',
    opcaoAnunciarSim: 'Sim, quero anunciar',
    opcaoAnunciarNao: 'Só quero saber o valor',
    ctaWhatsapp: 'Falar com um corretor',
    mensagemSucesso: 'Estimativa pronta! Um corretor pode confirmar o valor com uma visita.',
    consentText:
        'Autorizo o contato e o tratamento dos meus dados conforme a Política de Privacidade.',
    leadOrigin: 'Avaliação de Imóvel',
};

/** `<div … class="…itp-avm-calc…" …>` — a ordem dos atributos do builder pode mudar. */
const RE_ABERTURA = /<div\b[^>]*\bclass\s*=\s*"[^"]*\bitp-avm-calc\b[^"]*"[^>]*>/i;
const RE_DATA_AVM = /\bdata-avm\s*=\s*"([^"]*)"/i;
/** varredura de <div …> e </div> a partir do fim da tag de abertura. */
const RE_TAG_DIV = /<div\b[^>]*>|<\/div\s*>/gi;

/**
 * Desfaz o escape de ATRIBUTO HTML que o builder aplica no JSON.
 * `&amp;` vem por ÚLTIMO de propósito: senão `&amp;quot;` (um `&quot;` literal no texto)
 * viraria uma aspa de verdade e quebraria o JSON.
 */
export function desescaparAtributoHtml(valor: string): string {
    return String(valor)
        .replace(/&quot;/g, '"')
        .replace(/&#0*34;/g, '"')
        .replace(/&#0*39;/g, "'")
        .replace(/&apos;/g, "'")
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&amp;/g, '&');
}

function texto(valor: unknown, padrao: string): string {
    return typeof valor === 'string' && valor.trim() ? valor.trim() : padrao;
}

/**
 * Cor só entra no `style` do wrapper (custom properties) se for reconhecível.
 * Não é XSS (o React usa setProperty), mas lixo no CSS quebraria o visual em silêncio.
 */
function cor(valor: unknown, padrao: string): string {
    const v = typeof valor === 'string' ? valor.trim() : '';
    if (!v || v.length > 32) return padrao;
    if (/^#[0-9a-f]{3,8}$/i.test(v)) return v;
    if (/^rgba?\(\s*[\d.\s,%/]+\)$/i.test(v)) return v;
    if (/^hsla?\(\s*[\d.\s,%/deg]+\)$/i.test(v)) return v;
    if (/^[a-z]{3,20}$/i.test(v)) return v; // nomes CSS (white, black, …)
    return padrao;
}

function inteiroOuNulo(valor: unknown): number | null {
    const n = typeof valor === 'number' ? valor : parseInt(String(valor ?? ''), 10);
    return Number.isFinite(n) && Number.isInteger(n) ? n : null;
}

/** Aplica os defaults do contrato sobre o JSON cru. Devolve `null` se o mínimo não vier. */
export function normalizarAvmConfig(cru: unknown): AvmConfig | null {
    if (!cru || typeof cru !== 'object' || Array.isArray(cru)) return null;
    const o = cru as Record<string, unknown>;

    if (Number(o.v) !== 1) return null;

    const codsite = inteiroOuNulo(o.codsite);
    if (!codsite || codsite <= 0) return null;

    // token vazio = tenant sem a avaliação habilitada (o builder serializa `token: ""`).
    // Nesse caso o site mostra o FALLBACK do placeholder, não o wizard.
    if (typeof o.token !== 'string' || !o.token.trim()) return null;

    const temaCru = (o.theme && typeof o.theme === 'object' ? o.theme : {}) as Record<string, unknown>;

    return {
        v: 1,
        codsite,
        token: o.token.trim(),
        pageId: inteiroOuNulo(o.pageId),
        blockId: typeof o.blockId === 'string' && o.blockId.trim() ? o.blockId.trim() : null,
        titulo: texto(o.titulo, AVM_TEXTOS_PADRAO.titulo),
        subtitulo: texto(o.subtitulo, AVM_TEXTOS_PADRAO.subtitulo),
        perguntaAnunciar: texto(o.perguntaAnunciar, AVM_TEXTOS_PADRAO.perguntaAnunciar),
        opcaoAnunciarSim: texto(o.opcaoAnunciarSim, AVM_TEXTOS_PADRAO.opcaoAnunciarSim),
        opcaoAnunciarNao: texto(o.opcaoAnunciarNao, AVM_TEXTOS_PADRAO.opcaoAnunciarNao),
        ctaWhatsapp: texto(o.ctaWhatsapp, AVM_TEXTOS_PADRAO.ctaWhatsapp),
        mensagemSucesso: texto(o.mensagemSucesso, AVM_TEXTOS_PADRAO.mensagemSucesso),
        consentText: texto(o.consentText, AVM_TEXTOS_PADRAO.consentText),
        leadOrigin: texto(o.leadOrigin, AVM_TEXTOS_PADRAO.leadOrigin),
        theme: {
            primary: cor(temaCru.primary, AVM_THEME_PADRAO.primary),
            secondary: cor(temaCru.secondary, AVM_THEME_PADRAO.secondary),
            bg: cor(temaCru.bg, AVM_THEME_PADRAO.bg),
            text: cor(temaCru.text, AVM_THEME_PADRAO.text),
            surface: cor(temaCru.surface, AVM_THEME_PADRAO.surface),
            muted: cor(temaCru.muted, AVM_THEME_PADRAO.muted),
        },
    };
}

/**
 * Acha o PRIMEIRO placeholder e devolve o HTML de antes, a config e o HTML de depois.
 * Nada encontrado / inválido → `null` (a página cai no comportamento de hoje, byte a byte).
 */
export function extrairAvmPlaceholder(html: unknown): AvmSplit | null {
    if (typeof html !== 'string' || html.indexOf('itp-avm-calc') === -1) return null;

    const abertura = RE_ABERTURA.exec(html);
    if (!abertura || abertura.index == null) return null;

    const tagAbertura = abertura[0];
    const dataAvm = RE_DATA_AVM.exec(tagAbertura);
    if (!dataAvm) return null;

    let cru: unknown;
    try {
        cru = JSON.parse(desescaparAtributoHtml(dataAvm[1]));
    } catch {
        return null;
    }

    const config = normalizarAvmConfig(cru);
    if (!config) return null;

    // Fim do bloco: conta <div>/</div> a partir do fim da tag de abertura. O contrato garante
    // 1 div interno (o fallback), mas contar também aguenta 0 ou espaços entre os fechamentos.
    const inicioConteudo = abertura.index + tagAbertura.length;
    RE_TAG_DIV.lastIndex = inicioConteudo;
    let profundidade = 1;
    let fim = -1;
    let tag: RegExpExecArray | null;
    while ((tag = RE_TAG_DIV.exec(html)) !== null) {
        if (tag[0].charAt(1) === '/') {
            profundidade -= 1;
            if (profundidade === 0) {
                fim = tag.index + tag[0].length;
                break;
            }
        } else {
            profundidade += 1;
        }
    }
    RE_TAG_DIV.lastIndex = 0;
    if (fim === -1) return null; // placeholder não fechado: não mexe no HTML

    return {
        antes: html.slice(0, abertura.index),
        config,
        depois: html.slice(fim),
    };
}
